import React from 'react';
import PropTypes from 'prop-types';
import CSSModules from 'react-css-modules';
import {connect} from 'react-redux';
import FormInput from 'components/common/material-form/Input';
import RaisedButton from 'components/common/material/RaisedButton';
import {reduxForm, Field} from 'redux-form';
import {ValidationsUtils} from 'utils/validationUtils';
import {FormattedMessage, injectIntl, intlShape} from 'react-intl';
import styles from './create-password.scss';
import SignUpConsent from 'auth/SignUpConsent';
import {agreeAndSignUpText} from 'auth/staticComponents';
import Tooltip from 'components/common/material/Tooltip';
import IconButton from 'components/common/material/IconButton';
import reduxRouteChanged from "../../../services/reduxRouteChanged";
import {createPassword, createPasswordResetAction} from "../../../auth/authWithOtp/redux2";
import {replace} from "react-router-redux";
import {passwordUpdated} from "../../../utils/url/passwordUpdated";
import LoadingBlocking from "../../../components/loading/LoadingBlocking";
import Layout from "../../../auth/Layout";
import {datadogRum} from "@datadog/browser-rum";
import loginUrl from "../../../utils/url/loginUrl";
import mixpanelService from "../../../services/mixpanel";


const IconButtonTooltip = Tooltip(IconButton);

function validate(values, props) {
  // intl comes from @injectIntl which has to be called before @reduxForm in order to work
  const {intl: {formatMessage}} = props;
  const errorKeys = {};

  ValidationsUtils.required('password', values, errorKeys);
  ValidationsUtils.required('verifyPassword', values, errorKeys);
  ValidationsUtils.passwordRules('password', values, errorKeys);
  ValidationsUtils.verifyPassword('verifyPassword', 'password', values, errorKeys);

  const errors = Object.keys(errorKeys).reduce((mappedErrors, errorField) => {
    if (errorKeys[errorField]) {
      const passwordRulesError = ['validations.passwordRules.length', 'validations.passwordRules.lowercase', 'validations.passwordRules.uppercase', 'validations.passwordRules.digit', 'validations.passwordRules.special'];

      if (passwordRulesError.includes(errorKeys[errorField])) {
        mappedErrors[errorField] = formatMessage({id: 'validations.passwordRules.general'});
      } else {
        mappedErrors[errorField] = formatMessage({id: errorKeys[errorField]});
      }
    }
    return mappedErrors;
  }, {});

  return errors;
}

@injectIntl
@connect(
  state => ({
    statusText: state.authRoot.register.statusText,
    email: state.routing.locationBeforeTransitions.query.email,
    firstName: state.authRoot.register.firstName,
    otpToken: state.authRoot.register.otpToken,
    emailRegistrationForm: state.form.emailRegistrationForm,
    isLoadingCreatePassword: state.authRoot.register.isLoadingCreatePassword,
    isCreatePasswordSuccess: state.authRoot.register.isCreatePasswordSuccess,
    isCreatePasswordFail: state.authRoot.register.isCreatePasswordFail,
    registerCreatePasswordError: state.authRoot.register.registerCreatePasswordError,
  }),
  dispatch => ({
    createPassword: (password, token) => dispatch(createPassword(password, token)),
    redirect: (path) => dispatch(replace(path)),
  })
)
@reduxForm({
  form: 'emailRegistrationForm',
  validate
})
@CSSModules(styles)
class CreatePassword extends React.Component {
  static propTypes = {
    intl: intlShape,
    pristine: PropTypes.bool,
    isLoadingCreatePassword: PropTypes.bool,
    isCreatePasswordSuccess: PropTypes.bool,
    isCreatePasswordFail: PropTypes.bool,
    registerCreatePasswordError: PropTypes.object,
    valid: PropTypes.bool,
    statusText: PropTypes.string,
    email: PropTypes.string,
    firstName: PropTypes.string,
    otpToken: PropTypes.string,
    createPassword: PropTypes.func,
    handleSubmit: PropTypes.func,
    redirect: PropTypes.func,
    emailRegistrationForm: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.submit = this.submit.bind(this);
  }

  componentDidMount() {
    if (!this.props.otpToken){
      this.props.redirect(loginUrl());
    }
  }

  submit() {
    if (this.props.emailRegistrationForm && this.props.emailRegistrationForm.values) {
      this.props.createPassword(this.props.emailRegistrationForm.values.password, this.props.otpToken);
    }
  }

  componentDidUpdate(prevProps) {
    const {isCreatePasswordSuccess, isCreatePasswordFail, registerCreatePasswordError} = this.props;
    if (isCreatePasswordSuccess) {
      datadogRum.addUserAction('Sign up create password success', {});
      mixpanelService.track('OTP sign up', {step: 'create password success'});
      this.props.redirect(passwordUpdated());
    } else if (!prevProps.isCreatePasswordFail && isCreatePasswordFail) {
      datadogRum.addUserAction('Sign up create password fail', { registerCreatePasswordError });
      mixpanelService.track('OTP sign up', {step: 'create password failed'});
    }
  }

  render() {
    const { pristine, isLoadingCreatePassword, valid, intl: {formatMessage}, firstName, statusText} = this.props;
    return (
      <Layout>
        {isLoadingCreatePassword && <LoadingBlocking/>}
        <div styleName="title">Welcome {firstName}</div>
        <div styleName="subTitle" data-at="registrationFormTxt">
          <FormattedMessage id="passwordUpdated.createPassword.message"/>
        </div>

        {statusText && <div styleName="message">{this.props.statusText}</div>}
        <form role="form" onSubmit={this.props.handleSubmit(this.submit)}>
          <Field
            type="password"
            data-at="createPasswordInput"
            name="password"
            component={FormInput}
            hint={formatMessage({id: 'auth.createPassword'})}
            label={formatMessage({id: 'auth.createPassword'})}
            fullWidth={true}>
          <IconButtonTooltip styleName={'toolTipEmailRegistration'}
                             tooltipShowOnClick={true}
                             tooltipPosition="left"
                             icon="info_outline"
                             theme={{tooltip: styles.toolTipPasswordToolbox }}
                             tooltip={formatMessage({id: 'auth.login.email.toolTip'})} />
          </Field>
          <Field
            type="password"
            data-at="repeatPasswordInput"
            name="verifyPassword"
            component={FormInput}
            hint={formatMessage({id: 'auth.repeatPassword'})}
            label={formatMessage({id: 'auth.repeatPassword'})}
            fullWidth={true}
          />
          <SignUpConsent buttonConfirmText={agreeAndSignUpText} />
          <br/>
          <br/>
          <RaisedButton data-at="acceptSignUp"
                        primary={true}
                        label={formatMessage({id: 'registration.button.signUp'})}
                        fullWidth={true}
                        type="submit"
                        disabled={pristine || isLoadingCreatePassword || !valid} />
        </form>
      </Layout>
    );
  }
}

function initStore(dispatch) {
  dispatch(createPasswordResetAction());
}

export default reduxRouteChanged(initStore, CreatePassword);



// WEBPACK FOOTER //
// ./src/containers/auth/create-password/CreatePassword.js